import ENV from '@/config/runtime-env'

/**
 * DF-515: the testers' time machine. Outside production, a tester can jump
 * the portal forward in time from the test clock bar; the browser's clock and
 * every API request (via a header the backend honours) then run at that time,
 * still ticking. The jump is kept in this browser only, so testers don't
 * change each other's clock. Forward only, and at most 89 days (sign-in
 * tokens last 90). Production ignores all of it.
 */

const STORAGE_KEY = 'portal-time-offset'

/** The header carrying the jump, in seconds (App\Http\Middleware\TimeTravel). */
export const TIME_OFFSET_HEADER = 'X-Portal-Time-Offset'

export const MAX_OFFSET_MS = 89 * 24 * 60 * 60 * 1000

/** Never in production; an environment that doesn't say is treated as production. */
export const TIME_TRAVEL_ENABLED = ENV.environment !== 'production'

/** The browser's own clock, before any jump. */
export const RealDate = Date

/** The current jump in milliseconds: 0 when there's none, or in production. */
export const timeOffsetMs = (): number => {
  if (!TIME_TRAVEL_ENABLED) return 0
  try {
    const value = Number(localStorage.getItem(STORAGE_KEY))
    return Number.isFinite(value) && value > 0 ? Math.min(value, MAX_OFFSET_MS) : 0
  } catch {
    return 0
  }
}

/** Save a new jump (0 clears it). The page reloads afterwards to apply it. */
export const setTimeOffsetMs = (ms: number): void => {
  try {
    if (ms > 0) localStorage.setItem(STORAGE_KEY, String(Math.min(ms, MAX_OFFSET_MS)))
    else localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Storage blocked: nothing to remember, so the portal stays on real time.
  }
}

/**
 * Shift the browser's clock by the saved jump. Runs once, before anything else
 * loads, so every `new Date()` and `Date.now()` sees the travelled time.
 * Fixed dates (`new Date('2026-12-22')`) are untouched.
 */
export const installTimeTravel = (): void => {
  const offset = timeOffsetMs()
  if (offset === 0) return

  class ShiftedDate extends RealDate {
    constructor(...args: unknown[]) {
      if (args.length === 0) super(RealDate.now() + offset)
      else super(...(args as [number]))
    }

    static now(): number {
      return RealDate.now() + offset
    }
  }

  window.Date = ShiftedDate as DateConstructor
}
